import axios, { AxiosRequestConfig, AxiosInstance, AxiosError } from 'axios';
import axiosRetry, { IAxiosRetryConfig } from 'axios-retry';
import authService from '../services/AuthService';

// domains of environments (hardcoded - react doesn't allow changes on runtime)
const devApi = 'dev.amazonpreconnector.com';
const testApi = 'test.amazonpreconnector.com';
const productionApi = 'amazonpreconnector.com';

let adminBaseURL = process.env.REACT_APP_ADMIN_API_URL;
let proxyBaseURL = process.env.REACT_APP_PROXY_API_URL;
let reportBaseURL = process.env.REACT_APP_REPORT_API_URL;
let workDocsEnabled = true;
if (window.location.hostname.endsWith(devApi)) {
    adminBaseURL = adminBaseURL || `https://admin.api.${devApi}`;
    proxyBaseURL = proxyBaseURL || `https://proxy.api.${devApi}`;
    reportBaseURL = reportBaseURL || `https://report.api.${devApi}`;
} else if (!adminBaseURL && window.location.hostname.endsWith(testApi)) {
    adminBaseURL = adminBaseURL || `https://admin.api.${testApi}`;
    proxyBaseURL = proxyBaseURL || `https://proxy.api.${testApi}`;
    reportBaseURL = reportBaseURL || `https://report.api.${testApi}`;
} else if (!adminBaseURL && window.location.hostname.endsWith(productionApi)) {
    adminBaseURL = adminBaseURL || `https://admin.api.${productionApi}`;
    proxyBaseURL = proxyBaseURL || `https://proxy.api.${productionApi}`;
    reportBaseURL = reportBaseURL || `https://report.api.${productionApi}`;
}
export const adminApiBaseURL = adminBaseURL;
export const proxyApiBaseURL = proxyBaseURL;
export const reportApiBaseURL = reportBaseURL;
export const workDocsOAuth2Enabled = workDocsEnabled;

/*** AUTH API ***/

// config
const authAxiosConfig: AxiosRequestConfig = {
    baseURL: adminApiBaseURL,
    withCredentials: true,
};

// response rejected interceptor
const axiosResponseRejectedInterceptor = (error: any) => {
    if (error && error.response && error.response.status === 401 && !!authService.auth) {
        window.location.href = `/login`;
    } else {
        return Promise.reject(error);
    }
};

// create axios http client
const authAxiosInstance: AxiosInstance = axios.create(authAxiosConfig);
authAxiosInstance.interceptors.response.use(undefined, axiosResponseRejectedInterceptor);
export const authAxios = authAxiosInstance;

/****** RESOURCES APIS ***/

// request interceptor
const axiosRequestInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig | Promise<AxiosRequestConfig> => {
    return {
        ...config,
        headers: {
            Authorization: authService.accessToken ? `Bearer ${authService.accessToken}` : null,
            ...config.headers,
        },
    };
};

// retry configuration
const retryConfig: IAxiosRetryConfig = {
    retries: 4,
    retryCondition: (error: AxiosError): boolean => {
        let retry: boolean = false;
        if (error && error.config && error.response && error.response.status === 401 && authService.accessToken) {
            const retryCount = (error as any).config['axios-retry'].retryCount;
            if (retryCount === 0) {
                authService.refreshAccessToken();
                retry = true;
            } else if (retryCount < 4) {
                error.config.headers.Authorization = `Bearer ${authService.accessToken}`;
                retry = true;
            }
        }
        return retry;
    },
    retryDelay: (retryNumber = 0): number => {
        return 750 * retryNumber;
    },
};

/*** ADMIN API ***/

// config
const adminApiAxiosConfig: AxiosRequestConfig = {
    baseURL: adminApiBaseURL,
};

// create axios http client
const adminApiAxios: AxiosInstance = axios.create(adminApiAxiosConfig);
adminApiAxios.interceptors.request.use(axiosRequestInterceptor);
axiosRetry(adminApiAxios, retryConfig);
export default adminApiAxios;

/*** PROXY API ***/

// config
const proxyApiAxiosConfig: AxiosRequestConfig = {
    baseURL: proxyBaseURL,
};

// create axios http client
const proxyApiAxiosInstance: AxiosInstance = axios.create(proxyApiAxiosConfig);
proxyApiAxiosInstance.interceptors.request.use(axiosRequestInterceptor);
axiosRetry(proxyApiAxiosInstance, retryConfig);
export const proxyApiAxios = proxyApiAxiosInstance;

/*** REPORT API ***/

// config
const reportApiAxiosConfig: AxiosRequestConfig = {
    baseURL: reportBaseURL,
};

// create axios http client
const reportApiAxiosInstance: AxiosInstance = axios.create(reportApiAxiosConfig);
reportApiAxiosInstance.interceptors.request.use(axiosRequestInterceptor);
axiosRetry(reportApiAxiosInstance, retryConfig);
export const reportApiAxios = reportApiAxiosInstance;
